import { useHttpClient } from '@/composables/useHttpClient'
import { useLogError } from '@/composables/useLogError'
import { useCubeStore } from '@/stores/useCubeStore'
import { useFormsStore } from '@/stores/useFormsStore'
import { NumberUtil } from '@/util/NumberUtil'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAccountStore } from './useAccountStore'

export interface Booking {
  bookedOn: string
  validFrom: string
  validTo: string
  article: ShopArticle
}

export interface ShopArticle {
  articleId: number
  articleType: number
  articleGroupId: ArticleGroup
  cancellationDate?: Date
  cancellationNotice: number
  commitmentPeriod?: Date
  containedArticles: ContainedShopArticle[]
  description: string
  directCancellation: boolean
  directOrder: boolean
  iconUrl: string
  maxNumBookings: number
  name: string
  nextVatPrice: number | null
  numEnergyLines: number
  standardPrice: number
  vat: number
  vatPrice: number
}

export interface ContainedShopArticle {
  articleId: number
  quantity: number
}

export enum ArticleGroup {
  Abgaben = 3,
  Antennen = 7,
  BasisTarifartikel = 2,
  Beteiligungsprojekte = 31,
  Cube = 18,
  Cubetarife = 4,
  Dienstleistung = 32,
  eFRIENDS_Hardware = 17,
  eFRIENDS_StartpaketPlus = 1,
  EnergyControl_Paket = 25,
  Gehäuse = 22,
  Kabel_Adapter = 5,
  Klappwandler = 15,
  Koaxial_Kabel = 26,
  Leitungsschutzschalter = 24,
  Meter = 19,
  Netzteile = 12,
  Netzwerk_Aktiv = 8,
  Netzwerk_Passiv = 28,
  Partnerpakete = 30,
  Patchkabel = 21,
  Peripherie = 6,
  Router_Modems = 9,
  Schukoverteiler = 11,
  Serviceartikel = 16,
  Shopartikel = 33,
  SIM_Karten = 29,
  SMA_Adapter = 27,
  Stromversorgung = 23,
  Switch = 10,
  USB_Kabel = 13,
  Verteilerschränke = 14,
  Watch = 20
}

// --- Normalizers ---

function stringToDate(dateString: string | undefined) {
  if (!dateString) {
    return undefined
  }

  return new Date(dateString)
}

function normalizeArticle(article: any) {
  return {
    ...article,
    cancellationDate: stringToDate(article?.cancellationDate),
    commitmentPeriod: stringToDate(article?.commitmentPeriod)
  }
}

function normalizeArticles(articles: any[]) {
  return articles.map((article) => normalizeArticle(article))
}

function normalizeBookings(bookings: Booking[]) {
  return bookings.map((booking) => ({
    ...booking,
    bookedOn: stringToDate(booking.bookedOn),
    validFrom: stringToDate(booking.validFrom),
    validTo: stringToDate(booking.validTo),
    article: normalizeArticle(booking.article)
  }))
}

export const useShopStore = defineStore('shop', () => {
  const { logError } = useLogError()
  const { proxyApi } = useHttpClient()
  const accountStore = useAccountStore()

  // --- State ---

  const articles = ref<ShopArticle[]>([])
  const bookings = ref<any[]>([])
  const orders = ref<any[]>([])
  const error = ref(false)
  const inactive = ref(false)

  const isCommunityLess = ref(accountStore.properties.isCommunityless ?? false)

  // --- Computed ---

  const bookableArticles = computed(() =>
    articles.value.filter((article) => canBookArticle(article))
  )

  // --- Helpers ---

  function canBookArticle(article: ShopArticle): boolean {
    if (!article.directOrder) {
      return false
    }

    if (
      import.meta.env.VITE_SHOW_IGNORE_ARTICLE !== 'true' &&
      article.name.indexOf('IGNORE') > -1
    ) {
      return false
    }

    if (article.maxNumBookings < 0) {
      return true
    }

    if (isCommunityLess.value && article.articleGroupId != ArticleGroup.Shopartikel) {
      return false
    }

    return getNumBookings(article) < NumberUtil.parseFloat(article.maxNumBookings)
  }

  function getNumBookings(article: any): number {
    return bookings.value
      .filter((booking) => booking.article.articleId === article.articleId)
      .reduce((count, booking) => count + booking.count, 0)
  }

  function applyCustomerInfo(customerInfo: any) {
    if (!customerInfo) {
      return
    }
    const cubeStore = useCubeStore()
    // Update energy-line counts that change after a booking/cancellation
    cubeStore.unusedEnergyLines = parseInt(customerInfo.energyLinesAvailable)
    cubeStore.totalEnergyLinesAsConsumer = customerInfo.totalEnergyLinesAsConsumer
  }

  // --- Actions ---

  async function loadArticles() {
    const cubeStore = useCubeStore()
    try {
      const result = await proxyApi.get('articles', {
        params: { cubeName: cubeStore.name }
      })
      if (!result || !result.data || result.data.error) {
        throw new Error(`articles request returned with error: ${result.data.error?.Message}`)
      }
      error.value = false
      articles.value = normalizeArticles(result.data)
    } catch (err: any) {
      if (err.response?.status === 406) {
        inactive.value = true
        if (err.response.data) {
          articles.value = normalizeArticles(err.response.data)
        }
        return
      }
      logError(`Requesting articles failed: ${err}`)
      error.value = true
    }
  }

  async function loadBookings() {
    const cubeStore = useCubeStore()
    try {
      const result = await proxyApi.get('booking', {
        params: { cubeName: cubeStore.name }
      })
      if (!result || !result.data || result.data.error) {
        throw new Error(`booking request returned with error: ${result.data.error?.Message}`)
      }
      error.value = false
      bookings.value = normalizeBookings(result.data)
    } catch (err) {
      logError(`Requesting booking failed: ${err}`)
      error.value = true
    }
  }

  async function loadOrders() {
    const cubeStore = useCubeStore()
    try {
      const result = await proxyApi.get('order', {
        params: { cubeName: cubeStore.name }
      })
      if (!result || !result.data || result.data.error) {
        throw new Error(`order request returned with error: ${result.data.error?.Message}`)
      }
      error.value = false
      orders.value = normalizeBookings(result.data)
    } catch (err) {
      logError(`Requesting order failed: ${err}`)
      error.value = true
    }
  }

  async function load() {
    await Promise.all([loadArticles(), loadBookings(), loadOrders()])
  }

  async function bookArticle({ articleId, consent }: { articleId: number; consent: boolean }) {
    const formsStore = useFormsStore()
    const cubeStore = useCubeStore()
    try {
      const result = await proxyApi.post('booking', {
        cubeName: cubeStore.name,
        articleId,
        consent: consent.toString()
      })
      formsStore.setFormState(`buy-${articleId}`, 'finished')
      applyCustomerInfo(result.data.customerInfo)
    } catch (err) {
      logError(`Creating a booking failed: ${err}`)
      formsStore.setFormState(`buy-${articleId}`, 'error')
    }
  }

  async function cancelBooking(bookingId: number) {
    const formsStore = useFormsStore()
    const cubeStore = useCubeStore()
    try {
      const result = await proxyApi.delete('booking', {
        params: { cubeName: cubeStore.name, bookingId }
      })
      formsStore.setFormState(`cancel-${bookingId}`, 'finished')
      applyCustomerInfo(result.data.customerInfo)
      bookings.value = normalizeBookings(result.data.list)
    } catch (err) {
      logError(`Deleting a booking failed: ${err}`)
      formsStore.setFormState(`cancel-${bookingId}`, 'error')
    }
  }

  function getArticle(id: number) {
    return articles.value.find((article) => article.articleId === id)
  }

  function getBooking(bookingId: number) {
    return bookings.value.find((booking) => booking.bookingId === bookingId)
  }

  function getOrder(orderId: number) {
    return orders.value.find((order) => order.bookingId === orderId)
  }

  function getArticleEnergyLines(id: number): number {
    const article = getArticle(id)
    if (!article) {
      return 0
    }
    return article.numEnergyLines
  }

  return {
    // State
    articles,
    bookings,
    orders,
    error,
    inactive,
    // Computed
    bookableArticles,
    // Actions
    load,
    loadArticles,
    loadBookings,
    loadOrders,
    bookArticle,
    cancelBooking,
    // Getters
    getArticle,
    getBooking,
    getOrder,
    getArticleEnergyLines,
    canBookArticle,
    getNumBookings
  }
})
